﻿import { useCallback, useEffect, useMemo, useState } from 'react';
import { LogIn } from 'lucide-react';import Sidebar from './components/sidebar/Sidebar';
import ChatArea from './components/center/ChatArea';
import RightPanel from './components/right/RightPanel';
import WelcomeModal, {
  markWelcomeSeen,
  shouldShowWelcome,
} from './components/center/WelcomeModal';
import { useAuth } from './hooks/useAuth';
import { useChatSocket } from './hooks/useChatSocket';
import { useInbox } from './hooks/useInbox';
import { usePartnerTyping } from './hooks/usePartnerTyping';
import { useMessages } from './hooks/useMessages';
import { useSellerPanel } from './hooks/useSellerPanel';
import { useTheme } from './hooks/useTheme';
import { useViewportHeight } from './hooks/useViewportHeight';

/**
 * Z2Market chat - three-column shell, wired to the live Express/Prisma backend.
 *
 * Auth is the existing httpOnly `z2_access_token` cookie: `useAuth` bootstraps
 * the user from GET /api/auth/me and every request is same-origin, so there is
 * no token handling anywhere in this app.
 *
 * One WebSocket is opened here and shared: `useInbox` and `useMessages` both
 * subscribe to it rather than each opening their own connection.
 */
function SignInRequired() {
  return (
    <div className="flex h-full items-center justify-center bg-canvas px-6">
      <div className="w-full max-w-[380px] rounded-2xl border border-line bg-surface p-7 text-center shadow-soft">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-soft text-accent">
          <LogIn size={20} />
        </span>
        <h1 className="mt-4 text-[16px] font-semibold text-ink">Sign in to use chat</h1>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-2">
          Your messages, orders and offers are tied to your Z2Market account.
        </p>
        <a
          // Absolute: this app runs on chat.z2market.com, which has no /login
          // route of its own. Sign-in happens on the main site, and the session
          // is shared because COOKIE_DOMAIN is set to .z2market.com.
          href="https://z2market.com/login?returnUrl=https%3A%2F%2Fchat.z2market.com%2F"
          className="z2-glow-hover mt-5 inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-[13px] font-semibold text-white transition hover:bg-brand-700"
        >
          Sign in on z2market.com
        </a>
      </div>
    </div>
  );
}

export default function App() {
  const theme = useTheme();
  const auth = useAuth();
  // Keeps the shell exactly as tall as the visible viewport, so the composer
  // stays on screen when a mobile keyboard opens.
  useViewportHeight();
  const socket = useChatSocket({ enabled: auth.isAuthenticated });

  const currentUserId = auth.user?.id;

  // The API scopes the inbox by the account role, so the side is derived, not chosen.
  const side = auth.user?.role === 'SELLER' ? 'seller' : 'buyer';

  const inbox = useInbox({
    enabled: auth.isAuthenticated,
    socket,
    currentUserId,
    side,
  });

  const thread = useMessages({
    conversationId: inbox.selectedId,
    socket,
    currentUserId,
  });

  const partner = inbox.selectedConversation;

  // "X is typingâ€¦" for the open thread, and the matching ping we send while
  // composing. Both ride the single shared socket.
  const partnerTyping = usePartnerTyping({
    socket,
    conversationId: inbox.selectedId,
    currentUserId,
  });
  const onTyping = useCallback(
    (isTyping) => {
      if (inbox.selectedId) socket.sendTyping(inbox.selectedId, isTyping);
    },
    [socket, inbox.selectedId],
  );

  const panel = useSellerPanel({
    partnerId: partner?.partnerId,
    username: partner?.username,
    enabled: auth.isAuthenticated && Boolean(partner),
    // A seller's counterparty is a buyer, who has no listings to show.
    includeListings: auth.user?.role !== 'SELLER',
  });

  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  // Which details view is showing: 'orders' | 'profile'. Held here (not in the
  // panel) so it survives the panel collapsing, the slide-over closing and a
  // conversation switch - the switch itself now lives at the top of the panel.
  const [detailTab, setDetailTab] = useState('orders');
  const [favorites, setFavorites] = useState(() => new Set());

  /**
   * Narrow screens show ONE pane at a time; from `md` (tablet) up the list and
   * the chat sit side by side. The details column only joins them from `lg`,
   * because three columns do not fit a portrait tablet - below that it stays a
   * slide-over. The panel carries its own My Orders / User Profile switch at
   * every width, so the slide-over is fully usable on its own.
   */
  const [mobilePane, setMobilePane] = useState('list'); // 'list' | 'chat' | 'details'

  useEffect(() => {
    if (inbox.selectedId) setMobilePane('chat');
  }, [inbox.selectedId]);

  const showList = mobilePane === 'list' || !partner;
  const showChat = Boolean(partner) && mobilePane === 'chat';
  const showDetails = Boolean(partner) && mobilePane === 'details';

  const openDetails = () => {
    setDetailsCollapsed(false); // never open the slide-over showing only the rail
    setMobilePane('details');
  };

  /**
   * Safety briefing on first visit each session. Informational only - dismissing
   * it changes nothing and no feature is gated behind it.
   */
  const [welcomeOpen, setWelcomeOpen] = useState(() => shouldShowWelcome());
  const dismissWelcome = () => {
    markWelcomeSeen();
    setWelcomeOpen(false);
  };

  const onToggleFavorite = useMemo(
    () => () => {
      const id = inbox.selectedId;
      if (!id) return;
      setFavorites((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [inbox.selectedId],
  );

  if (auth.isLoading) {
    return (
      <div className="grid h-full place-items-center bg-canvas">
        <div
          role="status"
          aria-live="polite"
          className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-brand-600"
        >
          <span className="sr-only">Loading your chatâ€¦</span>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <SignInRequired />;
  }

  return (
    <div className="relative flex h-full w-full overflow-hidden bg-canvas text-ink">
      {/* â”€â”€ Sidebar: full column on md+, the list pane below that â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {/* w-full below md so the list pane really fills the phone screen - the
          wrapper would otherwise shrink to the aside's fit-content width. */}
      <div className={`${showList ? 'flex' : 'hidden'} min-h-0 w-full md:w-auto md:flex`}>
        <Sidebar
          inbox={inbox}
          user={auth.user}
          socketStatus={socket.status}
          isDark={theme.isDark}
          onToggleTheme={theme.toggleTheme}
        />
      </div>

      {/* â”€â”€ Chat: the main pane on md+, its own pane below that â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div
        className={`${showChat ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-1 md:flex`}
      >
        <ChatArea
          conversation={partner}
          currentUser={auth.user}
          messages={thread.messages}
          threadStatus={thread.status}
          threadError={thread.error}
          sending={thread.sending}
          sendProgress={thread.progress}
          hasMore={thread.hasMore}
          onLoadOlder={thread.loadOlder}
          onSendMessage={thread.send}
          onClearSelection={inbox.clearSelection}
          isFavorite={inbox.selectedId ? favorites.has(inbox.selectedId) : false}
          onToggleFavorite={onToggleFavorite}
          profile={panel.profile}
          onOpenDetails={openDetails}
          // Nothing decorative may move behind the welcome modal.
          motionPaused={welcomeOpen}
          partnerTyping={partnerTyping}
          onTyping={onTyping}
        />
      </div>

      {/* â”€â”€ Details: static column on lg+, slide-over below that â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {showDetails && (
        <button
          type="button"
          aria-label="Close details"
          onClick={() => setMobilePane('chat')}
          className="absolute inset-0 z-40 cursor-default bg-overlay lg:hidden"
        />
      )}
      {/* `absolute`, not `fixed`: the slide-over must be measured against the
          keyboard-aware shell rather than the layout viewport. */}
      <div
        className={`${showDetails ? 'flex' : 'hidden'} absolute inset-y-0 right-0 z-50 shadow-lift lg:static lg:z-auto lg:flex lg:shadow-none`}
      >
        <RightPanel
          conversation={partner}
          collapsed={showDetails ? false : detailsCollapsed}
          onToggleCollapsed={() =>
            showDetails ? setMobilePane('chat') : setDetailsCollapsed((collapsed) => !collapsed)
          }
          detailTab={detailTab}
          onDetailTabChange={setDetailTab}
          panel={panel}
          viewerRole={auth.user?.role}
          viewerId={currentUserId}
        />
      </div>

      {welcomeOpen && <WelcomeModal open onClose={dismissWelcome} />}
    </div>
  );
}
